import { useState } from 'react';
import { cn } from '../../styles/lib/utils';
import {
  BOOLEAN_OPTIONS,
  type FilterForm,
  type FilterOperatorType,
  type FilterType,
  getFilterOperatorTypes,
  NULL_OPERATOR_IDS,
  OPERATOR_TYPES,
} from '../../utils/filterSettings';
import { ComboBox } from '../ComboBox';
import { DatePicker } from '../DatePicker';
import type { DatePickerValue } from '../DatePicker/utils';
import { Input } from '../Input';
import { AdvancedFilterActionMenu, type AdvancedFilterActionMenuAction } from './AdvancedFilterActionMenu';
import { AdvancedFilterOperatorSelector, AdvancedFilterOperatorText } from './components';
import { getFilterPosition, getGroupOperatorPath } from './utils';

export type AdvancedFilterLineProps = {
  form: FilterForm;
  fieldPath: string;
  onMakeLineGroup: (fieldPath: string) => void;
  onDuplicate: (fieldPath: string) => void;
  onDelete: (fieldPath: string) => void;
  filterTypes?: FilterType[];
  className?: string;
  shouldShowOperatorSelector?: boolean;
  shouldShowOperatorText?: boolean;
  parentDepth?: number;
};

function AdvancedFilterLine({
  form,
  fieldPath,
  onMakeLineGroup,
  onDuplicate,
  onDelete,
  filterTypes = [],
  className,
  shouldShowOperatorSelector = false,
  shouldShowOperatorText = false,
  parentDepth = 0,
}: AdvancedFilterLineProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuActions: AdvancedFilterActionMenuAction[] = [
    { label: 'Make filter group', icon: 'plus', onClick: () => onMakeLineGroup(fieldPath) },
    { label: 'Duplicate', icon: 'copy', onClick: () => onDuplicate(fieldPath) },
    { label: 'Delete', icon: 'trash', onClick: () => onDelete(fieldPath) },
  ];

  const operatorPath = getGroupOperatorPath(fieldPath);
  const filterName = `Filter ${getFilterPosition(fieldPath)}`;

  return (
    <div className={cn('flex w-fit gap-1 p-1', isMenuOpen && 'bg-blue-10 rounded-sm', className)}>
      <div className="relative flex items-center gap-2">
        {/* Operator selector - shown on first item when group has multiple items */}
        {shouldShowOperatorSelector && <AdvancedFilterOperatorSelector form={form} operatorPath={operatorPath} />}
        {/* Operator text - shown on non-first items in a group */}
        {shouldShowOperatorText && <AdvancedFilterOperatorText form={form} operatorPath={operatorPath} />}
        {/* Filter Type Selector */}
        <form.Field name={`${fieldPath}.filterType`}>
          {(field: any) => {
            const filterTypeValue = field.state.value as FilterType | undefined;
            const filterType = filterTypeValue?.id
              ? (filterTypes.find((ft) => ft.id === filterTypeValue.id) ?? filterTypeValue)
              : filterTypeValue;

            const comboBoxOptions = filterTypes.map((ft) => ({ value: ft.id, label: ft.label }));
            const fieldName = filterType?.label ? `${filterName}: ${filterType.label}` : filterName;
            return (
              <>
                <ComboBox
                  aria-label={`${filterName} column`}
                  options={comboBoxOptions}
                  value={filterType?.id || ''}
                  onValueChange={(val) => {
                    const selected = filterTypes.find((ft) => ft.id === val);
                    if (selected) {
                      field.handleChange(selected);
                      // Clear operatorType and value when filterType changes
                      if (selected.filterValueConfig?.type === 'boolean') {
                        form.setFieldValue(`${fieldPath}.operatorType`, OPERATOR_TYPES.BOOLEAN[0]);
                      } else {
                        form.setFieldValue(`${fieldPath}.operatorType`, undefined);
                      }
                      form.setFieldValue(`${fieldPath}.value`, undefined);
                    }
                  }}
                  placeholder="Select..."
                  searchPlaceholder="Search filter types..."
                  emptyText="No filter type found."
                  triggerClassName="h-6"
                />

                {/* Only show operator type and value when filter type is selected */}
                {filterType && (
                  <>
                    {/* Operator Type Selector */}
                    <form.Field name={`${fieldPath}.operatorType`}>
                      {(operatorField: any) => {
                        if (filterType.filterValueConfig?.type === 'boolean') {
                          return null;
                        }
                        // Use column-specific operator types, or TEXT as default (plus
                        // null-check operators for date filters with allowNull)
                        const availableOperatorTypes = getFilterOperatorTypes(filterType);
                        const operatorType = operatorField.state.value as FilterOperatorType | undefined;

                        const comboBoxOptions = availableOperatorTypes.map((ot) => ({
                          value: ot.id,
                          label: ot.label,
                        }));
                        return (
                          <ComboBox
                            aria-label={`${fieldName} operator`}
                            options={comboBoxOptions}
                            value={operatorType?.id || ''}
                            onValueChange={(val) => {
                              const selected = availableOperatorTypes.find((ot) => ot.id === val);
                              if (selected) {
                                operatorField.handleChange(selected);
                                // drop value when switching to null-check operator
                                if (NULL_OPERATOR_IDS.has(selected.id)) {
                                  form.setFieldValue(`${fieldPath}.value`, undefined);
                                }
                              }
                            }}
                            placeholder="Select..."
                            searchPlaceholder="Search operators..."
                            emptyText="No operator found."
                            triggerClassName="h-6"
                          />
                        );
                      }}
                    </form.Field>

                    {/* Value Field - hidden for null-check operators (e.g. Is Empty), which need no value */}
                    <form.Field name={`${fieldPath}.operatorType`}>
                      {(operatorTypeField: any) => {
                        const currentOperatorType = operatorTypeField.state.value as FilterOperatorType | undefined;
                        if (currentOperatorType && NULL_OPERATOR_IDS.has(currentOperatorType.id)) {
                          return null;
                        }
                        return (
                          <form.Field name={`${fieldPath}.value`}>
                            {(valueField: any) => {
                              // Use column-specific value config, or default to input field
                              const valueConfig = filterType.filterValueConfig;

                              // Select value (ComboBox)
                              if (valueConfig?.type === 'select') {
                                return (
                                  <ComboBox
                                    aria-label={`${fieldName} value`}
                                    options={valueConfig.options}
                                    value={valueField.state.value || ''}
                                    onValueChange={(val) => valueField.handleChange(val)}
                                    placeholder="Select..."
                                    searchPlaceholder="Search options..."
                                    emptyText="No option found."
                                    triggerClassName="h-6 w-[180px]"
                                  />
                                );
                              }

                              if (valueConfig?.type === 'boolean') {
                                return (
                                  <ComboBox
                                    aria-label={`${fieldName} value`}
                                    options={BOOLEAN_OPTIONS}
                                    value={valueField.state.value || ''}
                                    onValueChange={(val) => valueField.handleChange(val)}
                                    placeholder="Select..."
                                    isSearchable={false}
                                    triggerClassName="h-6 w-[180px]"
                                  />
                                );
                              }

                              // Date value
                              if (valueConfig?.type === 'date') {
                                const dateValue = valueField.state.value as string | undefined;
                                // Convert ISO string to DatePickerValue format
                                let pickerValue: DatePickerValue = null;
                                if (dateValue) {
                                  try {
                                    const date = new Date(dateValue);
                                    if (!Number.isNaN(date.getTime())) {
                                      pickerValue = {
                                        date: date.toISOString(),
                                        view: 'Day',
                                      };
                                    }
                                  } catch {
                                    // Invalid date string, keep as null
                                  }
                                }

                                return (
                                  <DatePicker
                                    value={pickerValue}
                                    onChange={(value) => {
                                      // Extract ISO string from DatePickerValue
                                      if (value && 'date' in value) {
                                        valueField.handleChange(value.date);
                                      } else {
                                        valueField.handleChange(undefined);
                                      }
                                    }}
                                    allowRange={false}
                                    multiView={false}
                                    placeholder="MM/DD/YYYY"
                                    className="w-[180px]"
                                    inputClassName="h-6"
                                    aria-label={`${fieldName} value`}
                                  />
                                );
                              }

                              // Default text value (also used for nested fields)
                              return (
                                <Input
                                  name={`${fieldPath}.value`}
                                  type="text"
                                  value={valueField.state.value || ''}
                                  onChange={(e) => valueField.handleChange(e.target.value)}
                                  onBlur={valueField.handleBlur}
                                  placeholder=""
                                  aria-label={`${fieldName} value`}
                                  className="h-6 w-[180px]"
                                />
                              );
                            }}
                          </form.Field>
                        );
                      }}
                    </form.Field>
                  </>
                )}
              </>
            );
          }}
        </form.Field>
      </div>
      {menuActions.length > 0 && (
        <AdvancedFilterActionMenu
          actions={menuActions}
          onOpenChange={setIsMenuOpen}
          depth={parentDepth}
          aria-label={`${filterName} actions`}
        />
      )}
    </div>
  );
}

export { AdvancedFilterLine };
